import React, { useId } from 'react';

/**
 * A labelled value on the profile page.
 *
 * The design draws all four of them — both names, the phone and the e-mail —
 * on the grey field ground it uses for a control the reader cannot type in,
 * and pins a banner underneath saying to reach support to have any of it
 * changed. That is not a placeholder for a form that was never finished: it is
 * the policy the app already runs. `components/Forms/ProfileForm.jsx` disables
 * the same four unless the profile is being completed for the first time, and
 * the server rejects an e-mail that belongs to someone else with a 409.
 *
 * So the page shows them, and the banner is the way to change them.
 *
 * `readOnly` rather than `disabled`: a disabled input is skipped by the tab
 * order and read out as unavailable, which is the wrong thing to say about a
 * value the reader is meant to check. Read-only keeps it focusable and
 * selectable — so the reader can copy their own phone number — and still
 * refuses every keystroke.
 *
 * The e-mail and the phone pass `dir="ltr"` so their runs keep their order,
 * which also turns `text-start` to the left; `rtl:tw-text-right` keeps them on
 * the page's reading edge, as the design draws them.
 */
const ReadonlyField = ({ label, value, type = 'text', dir }) => {
    const id = useId();

    return (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-[9px]">
            <label
                htmlFor={id}
                className="tw-text-[16px] tw-font-normal tw-leading-[1.6] tw-text-v2-student-ink"
            >
                {label}
            </label>

            <input
                id={id}
                type={type}
                readOnly
                dir={dir}
                value={value ?? ''}
                className={
                    'tw-h-[39.407px] tw-w-full tw-rounded-[8.757px] tw-px-[13px] ' +
                    'tw-border-[0.876px] tw-border-solid tw-border-v2-neutral-200 ' +
                    'tw-bg-v2-ground-300 tw-text-[16px] tw-leading-[1.6] tw-text-v2-student-field ' +
                    'tw-text-start rtl:tw-text-right focus-visible:tw-outline-none focus-visible:tw-ring-2 ' +
                    'focus-visible:tw-ring-v2-brand focus-visible:tw-ring-offset-1'
                }
            />
        </div>
    );
};

export default ReadonlyField;
